import { on } from '@ember/modifier';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { dropTask } from 'ember-concurrency';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import type CompanyModel from 'garaje/models/company';
import focusOnInsert from 'garaje/modifiers/focus-on-insert';
import type FlashMessagesService from 'garaje/services/flash-messages';

interface TermsOfServiceModalSignature {
  Args: {
    company: CompanyModel;
  };
  Element: HTMLDivElement;
}

/**
 * Terms of Service acceptance modal shown to Global Admins of a company that
 * has not yet accepted. Non-dismissable; records acceptance and reloads the
 * company so the modal clears.
 * @param {Company} company
 */
export default class TermsOfServiceModal extends Component<TermsOfServiceModalSignature> {
  @service declare flashMessages: FlashMessagesService;

  // Unique per instance so the dialog's accessible name resolves even if two
  // instances ever coexist in the DOM.
  titleId = `${guidFor(this)}-title`;

  acceptTask = dropTask(async (): Promise<void> => {
    const { company } = this.args;

    try {
      await company.acceptTermsOfService();
    } catch {
      this.flashMessages.showAndHideFlash('error', 'Error accepting Terms of Service.');
      return;
    }

    // Acceptance is persisted server-side; stamp the attr locally so the modal
    // gate clears immediately and a failed reload can't strand the admin.
    company.termsLastAcceptedAt = new Date();

    // reload() is best-effort reconciliation; the local stamp above already
    // cleared the gate, so a failure is a safe no-op and a full page load later
    // re-fetches the canonical value into a clean record.
    try {
      await company.reload();
    } catch {
      // no-op: the record refreshes on the next load
    }
  });

  <template>
    <ModalDialog
      @clickOutsideToClose={{false}}
      @overlayClass="ember-modal-overlay--fadeIn"
      @targetAttachment="center"
      @translucentOverlay={{true}}
      @containerClass="ember-modal-dialog--policy-review"
    >
      <FocusTrapper>
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={{this.titleId}}
          class="h-full flex items-stretch"
          data-test-terms-of-service-modal
          ...attributes
        >
          <div class="grow flex flex-col p-10">
            <div class="text-center mb-4">
              <img
                src="/assets/images/tos-acceptance-modal.svg"
                class="inline-block w-full max-w-[167px] h-auto"
                width="167"
                height="146"
                alt=""
              />
            </div>

            <h2 id={{this.titleId}} class="text-3xl text-center">
              About our Terms of&nbsp;Service
            </h2>

            <p class="text-center">
              By continuing to use Envoy, you agree to Envoy’s
              <a href="https://envoy.com/legal/terms-of-service" target="_blank" rel="noopener noreferrer">
                Terms of Service
                <span class="sr-only">(opens in a new tab)</span>
              </a>
            </p>
            <button
              type="button"
              class="btn btn--brand"
              disabled={{this.acceptTask.isRunning}}
              aria-busy={{if this.acceptTask.isRunning "true" "false"}}
              data-test-accept-terms-of-service
              {{focusOnInsert}}
              {{on "click" this.acceptTask.perform}}
            >
              {{if this.acceptTask.isRunning "Saving…" "Agree and continue"}}
            </button>
          </div>
        </div>
      </FocusTrapper>
    </ModalDialog>
  </template>
}
